import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  Stack,
  InputAdornment,
  Chip,
  Avatar,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { fetchContacts, createContact, updateContact, deleteContact } from '../store/slices/contactsSlice';
import type { Contact } from '../services/contactsService';
import { useConfirm } from '../contexts/ConfirmDialogContext';


interface FormData {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  position: string;
  address: string;
  type: string;
  notes: string;
}

const initialFormData: FormData = {
  user_id: '', // This should come from auth state
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  company: '',
  position: '',
  address: '',
  type: 'professional',
  notes: '',
};

const Contacts: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const confirm = useConfirm();
  const { contacts, loading, error } = useSelector((state: RootState) => state.contacts);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Contact | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );
    
const userId = user?.user_id;
useEffect(() => {
    
    // Fetch contacts for current user (should get userId from auth state)
    dispatch(fetchContacts());
    setFormData((prev) => ({ ...prev, user_id: userId || '' }));

  }, [dispatch, userId]);

  const filteredRows = searchQuery.trim()
    ? contacts.filter((row) => {
        const q = searchQuery.toLowerCase();
        return (
          row.first_name.toLowerCase().includes(q) ||
          row.last_name.toLowerCase().includes(q) ||
          (row.email && row.email.toLowerCase().includes(q)) ||
          (row.company && row.company.toLowerCase().includes(q)) ||
          row.type.toLowerCase().includes(q)
        );
      })
    : contacts;

  const handleOpenDialog = (item?: Contact) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        user_id: userId || '',
        first_name: item.first_name,
        last_name: item.last_name,
        email: item.email || '',
        phone: item.phone || '',
        company: item.company || '',
        position: item.position || '',
        address: item.address || '',
        type: item.type,
        notes: item.notes || '',
      });
    } else {
      setSelectedItem(null);
      setFormData(initialFormData);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async () => {
    try {
      if (selectedItem) {
        await dispatch(updateContact({ id: selectedItem.id, ...formData })).unwrap();
      } else {
        await dispatch(createContact(formData)).unwrap();
      }
      handleCloseDialog();
      // Refresh contacts list
      dispatch(fetchContacts());
    } catch (error) {
      console.error('Error saving contact:', error);
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm(t('contacts.confirmDeleteContact'));
    if (ok) {
      try {
        await dispatch(deleteContact({ contactId: id, userId: userId || '' })).unwrap();
        // Refresh contacts list
        dispatch(fetchContacts());
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    }
  };

  const getTypeColor = (type: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
    const map: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
      'Expert Witness': 'primary',
      'Court Official': 'secondary',
      'Opposing Counsel': 'warning',
      'Notary': 'info',
      'Mediator': 'success',
    };
    return map[type] || 'default';
  };

  const columns: GridColDef[] = [
    {
      field: 'name', headerName: t('contacts.name'), width: 220,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: 'secondary.main' }}>
            {params.row.first_name[0]}{params.row.last_name[0]}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{params.row.first_name} {params.row.last_name}</Typography>
            <Typography variant="caption" color="text.secondary">{params.row.company}</Typography>
          </Box>
        </Box>
      ),
    },
    { field: 'type', headerName: t('contacts.type'), width: 150, renderCell: (params: GridRenderCellParams) => <Chip label={params.row.type} color={getTypeColor(params.row.type)} size="small" /> },
    { field: 'email', headerName: t('common.email'), width: 200 },
    { field: 'phone', headerName: t('contacts.phone'), width: 150 },
    { field: 'notes', headerName: t('contacts.notes'), width: 200 },
    { field: 'created_at', headerName: t('common.createdAt'), width: 120 },
    {
      field: 'actions', headerName: t('common.actions'), width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title={t('contacts.call')}><IconButton size="small" color="primary"><PhoneIcon /></IconButton></Tooltip>
          <Tooltip title={t('contacts.email')}><IconButton size="small" color="primary"><EmailIcon /></IconButton></Tooltip>
          <Tooltip title={t('common.edit')}><IconButton size="small" onClick={() => handleOpenDialog(params.row)}><EditIcon /></IconButton></Tooltip>
          <Tooltip title={t('common.delete')}><IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}><DeleteIcon /></IconButton></Tooltip>
          <Tooltip title={t('common.edit')}><IconButton size="small" onClick={() => handleOpenDialog(params.row)}><EditIcon /></IconButton></Tooltip>
          <Tooltip title={t('common.delete')}><IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}><DeleteIcon /></IconButton></Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('navigation.contacts')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>{t('contacts.addContact')}</Button>
      </Box>

      <Card sx={{ mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <TextField fullWidth placeholder={t('contacts.searchPlaceholder')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }} sx={{ maxWidth: 600 }} />
        </Box>
      </Card>

      <Card>
        <DataGrid rows={filteredRows} columns={columns} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} pageSizeOptions={[10]} checkboxSelection disableRowSelectionOnClick autoHeight rowHeight={60} />
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedItem ? t('contacts.editContact') : t('contacts.addContact')}
          <IconButton onClick={handleCloseDialog}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label={t('contacts.firstName')} fullWidth value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} />
              <TextField label={t('contacts.lastName')} fullWidth value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label={t('common.email')} fullWidth type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              <TextField label={t('contacts.phone')} fullWidth value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label={t('contacts.company')} fullWidth value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
              <TextField select label={t('contacts.type')} fullWidth value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                <MenuItem value="Expert Witness">{t('contacts.typeExpertWitness')}</MenuItem>
                <MenuItem value="Court Official">{t('contacts.typeCourtOfficial')}</MenuItem>
                <MenuItem value="Opposing Counsel">{t('contacts.typeOpposingCounsel')}</MenuItem>
                <MenuItem value="Notary">{t('contacts.typeNotary')}</MenuItem>
                <MenuItem value="Mediator">{t('contacts.typeMediator')}</MenuItem>
                <MenuItem value="Other">{t('contacts.typeOther')}</MenuItem>
              </TextField>
            </Box>
            <TextField label={t('contacts.notes')} fullWidth multiline rows={3} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">{selectedItem ? t('common.update') : t('common.create')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Contacts;
